/**
 * Profile Questionnaire — pure view.
 *
 * Elm-ish doctrine:
 *   - No business logic.
 *   - No useState for domain state.
 *   - No API calls.
 *   - Only renders based on `model`; dispatches via callbacks.
 *
 * The view consumes the crosswalk-respecting single-source
 * `rolePresentation.ts` for all scenario labels.
 *
 * Inputs (gender → scenario → profile fields) are presented in that
 * order; per S005's "3 minutes" framing, the form is rendered
 * step-by-step rather than as one giant wall.
 *
 * B-2.B photo surface:
 *   - renders `persistedPhotos` (server-canonical) by position;
 *   - renders `transientPhotos` (pending local uploads);
 *   - add-photo affordance disabled at PHOTO_MAX_COUNT (6);
 *   - delete affordance per durable photo (kind='deleting' during
 *     optimistic in-flight);
 *   - the file input is the only place where the view allocates a
 *     `URL.createObjectURL` preview (via the injected
 *     `ObjectUrlPort`); the harness / reducer owns the lifecycle.
 */

import type { ReactNode } from 'react';
import type { ProfileFormModel, PhotoEntry } from './model';
import { visibleSlotCount, isPhotoSlotExhausted } from './model';
import { errorsForField } from './validation';
import {
  CITY_MVP,
  EDUCATION_TYPES,
  GENDER_OPTIONS,
  WORK_EDUCATION_SPHERES,
  MAX_BIO,
  MAX_DISPLAY_NAME,
  MAX_HEADLINE,
  MIN_AGE_LOCAL,
  MAX_AGE_LOCAL,
  FIELD_CODE_LABEL_RU,
} from './constants';
import {
  SCENARIO_ORDER,
  questionnaireLabelFor,
} from './rolePresentation';
import { CompletionModal } from './components/CompletionModal';
import {
  ACCEPTED_PHOTO_MIME_TYPES,
  PHOTO_MAX_COUNT,
  clientPreflightPhotoFile,
} from '@/api/profilePhotos';
import type { ObjectUrlPort } from '@/api/objectUrlPort';
import { SkillsSection } from './SkillsSection';
import './view.css';

/**
 * Render-prop callbacks the view needs from the harness. The harness
 * exposes stable `useCallback`-wrapped action functions; the view
 * only invokes them.
 */
export interface ProfileQuestionnaireViewProps {
  readonly model: ProfileFormModel;
  readonly onNameChanged: (value: string) => void;
  readonly onGenderChanged: (value: 'male' | 'female' | undefined) => void;
  readonly onScenarioChanged: (value: 'equal' | 'sponsor' | 'no_work' | undefined) => void;
  readonly onAgeChanged: (value: number | undefined) => void;
  readonly onCityChanged: (value: string) => void;
  readonly onHeadlineChanged: (value: string) => void;
  readonly onBioChanged: (value: string) => void;
  readonly onWorkSphereChanged: (value: string | undefined) => void;
  readonly onEducationSphereChanged: (value: string | undefined) => void;
  readonly onEducationTypeChanged: (value: string | undefined) => void;
  /**
   * Object-URL port for `URL.createObjectURL` allocation. The view
   * calls this on file selection; the harness / reducer revokes
   * via `RevokeObjectURLPreview` effects.
   */
  readonly objectUrlPort: ObjectUrlPort;
  readonly onPhotoFilesSelected: (entries: readonly PhotoEntry[]) => void;
  readonly onPhotoDeleteRequested: (photoID: string) => void;
  readonly onPhotoPreviewReleased: (localId: string) => void;
  readonly onPhotoUploadRequested: (localId: string) => void;
  readonly onSubmit: () => void;
  readonly onRetry: () => void;
  readonly onDismissCompletionModal: () => void;
  // B-2.C skills callbacks:
  readonly onSkillsTaxonomyLoad: () => void;
  readonly onSkillsProfileLoad: () => void;
  readonly onSkillSelected: (skillId: string) => void;
  readonly onSkillRemoved: (skillId: string) => void;
  readonly onSkillsSearchChanged: (value: string) => void;
  readonly onSkillsCategoryChanged: (value: string | null) => void;
  readonly onSkillsSaveRequested: () => void;
}

/**
 * Main Profile Questionnaire view.
 *
 * Renders the three progressive steps mirroring S003 / S004 / S005:
 *   1. gender
 *   2. scenario (after gender selected)
 *   3. remaining fields (after scenario selected)
 */
export function ProfileQuestionnaireView(props: ProfileQuestionnaireViewProps): ReactNode {
  const { model } = props;
  if (model.status === 'idle' || model.status === 'loading') {
    return <div className="pq-loading" data-testid="pq-loading">Загрузка...</div>;
  }
  if (model.status === 'error') {
    return (
      <div className="pq-error" role="alert" data-testid="pq-load-error">
        <p>{model.serverError ?? 'Не удалось загрузить анкету'}</p>
        <button type="button" className="pq-btn-secondary" onClick={props.onRetry}>
          Попробовать снова
        </button>
      </div>
    );
  }
  return <ReadyView {...props} />;
}

function ReadyView(props: ProfileQuestionnaireViewProps): ReactNode {
  const { model } = props;
  const form = model.form;
  const isSaving = model.status === 'saving';
  const showForm = form.gender !== undefined && form.scenario !== undefined;

  return (
    <div
      className="pq-form"
      data-testid="profile-questionnaire-form"
      data-mode={model.mode.kind}
      data-step={
        form.gender === undefined
          ? 'gender'
          : form.scenario === undefined
          ? 'scenario'
          : 'fields'
      }
    >
      <h1 className="pq-h1">Анкета</h1>
      <p className="pq-lede">Заполните анкету, чтобы вас могли найти в поиске.</p>

      {model.profileStatus === 'inactive' && (
        <div className="pq-guidance" data-testid="pq-activation-guidance">
          <p>ваша анкета активируется после заполнения данных</p>
        </div>
      )}

      {model.profileStatus === 'inactive' && model.missingActivationFields.length > 0 && (
        <ul className="pq-missing-list" data-testid="pq-missing-list">
          {model.missingActivationFields.map((code) => (
            <li key={code}>{FIELD_CODE_LABEL_RU[code] ?? code}</li>
          ))}
        </ul>
      )}

      <section className="pq-section" data-testid="pq-gender-section">
        <h2 className="pq-h2">Пол</h2>
        <GenderControl value={form.gender} onChange={props.onGenderChanged} disabled={isSaving} />
      </section>

      {form.gender !== undefined && (
        <section className="pq-section" data-testid="pq-scenario-section">
          <h2 className="pq-h2">Сценарий</h2>
          <ScenarioControl
            value={form.scenario}
            onChange={props.onScenarioChanged}
            disabled={isSaving}
          />
        </section>
      )}

      {showForm && <FieldsSection {...props} isSaving={isSaving} />}

      {showForm && (
        <SkillsSection
          model={model}
          disabled={isSaving}
          onSkillSelected={props.onSkillSelected}
          onSkillRemoved={props.onSkillRemoved}
          onSearchChanged={props.onSkillsSearchChanged}
          onCategoryChanged={props.onSkillsCategoryChanged}
          onRetryTaxonomy={props.onSkillsTaxonomyLoad}
          onRetryLoadSelection={props.onSkillsProfileLoad}
          onRetrySave={props.onSkillsSaveRequested}
          onSaveSelection={props.onSkillsSaveRequested}
        />
      )}

      <CompletionModal
        open={model.completionModalOpen}
        onDismiss={props.onDismissCompletionModal}
      />
    </div>
  );
}

function FieldsSection(props: ProfileQuestionnaireViewProps & { isSaving: boolean }): ReactNode {
  const { model, isSaving } = props;
  const form = model.form;
  return (
    <>
      <section className="pq-section" data-testid="pq-basic-section">
        <h2 className="pq-h2">Основное</h2>
        <NameControl
          value={form.display_name}
          onChange={props.onNameChanged}
          disabled={isSaving}
          error={errorsForField(model.validationErrors, 'display_name')[0]?.message}
        />
        <AgeControl
          value={form.age}
          onChange={props.onAgeChanged}
          disabled={isSaving}
          error={errorsForField(model.validationErrors, 'age')[0]?.message}
        />
        <CityControl
          value={form.city}
          onChange={props.onCityChanged}
          disabled={isSaving}
          error={errorsForField(model.validationErrors, 'city')[0]?.message}
        />
      </section>

      <section className="pq-section" data-testid="pq-spheres-section">
        <h2 className="pq-h2">Сферы (необязательно)</h2>
        <SelectControl
          id="pq-work-sphere"
          label="Сфера работы"
          value={form.work_sphere ?? ''}
          options={WORK_EDUCATION_SPHERES}
          onChange={props.onWorkSphereChanged}
          disabled={isSaving}
        />
        <SelectControl
          id="pq-education-sphere"
          label="Сфера образования"
          value={form.education_sphere ?? ''}
          options={WORK_EDUCATION_SPHERES}
          onChange={props.onEducationSphereChanged}
          disabled={isSaving}
        />
        <SelectControl
          id="pq-education-type"
          label="Уровень образования"
          value={form.education_type ?? ''}
          options={EDUCATION_TYPES}
          onChange={props.onEducationTypeChanged}
          disabled={isSaving}
        />
      </section>

      <section className="pq-section" data-testid="pq-bio-section">
        <h2 className="pq-h2">О себе</h2>
        <TextControl
          id="pq-headline"
          label="Заголовок"
          value={form.headline ?? ''}
          onChange={props.onHeadlineChanged}
          disabled={isSaving}
          error={errorsForField(model.validationErrors, 'headline')[0]?.message}
          maxLength={MAX_HEADLINE}
          placeholder="Коротко о себе"
        />
        <TextareaControl
          id="pq-bio"
          label="Описание"
          value={form.bio ?? ''}
          onChange={props.onBioChanged}
          disabled={isSaving}
          error={errorsForField(model.validationErrors, 'bio')[0]?.message}
          maxLength={MAX_BIO}
          placeholder="Расскажите о своих интересах"
        />
      </section>

      <PhotoSection {...props} isSaving={isSaving} />

      {model.serverError && (
        <div
          className="pq-error"
          role="alert"
          data-testid="pq-submit-error"
          data-error-code={model.serverErrorCode ?? 'unknown'}
        >
          <p>{model.serverError}</p>
        </div>
      )}

      <div className="pq-actions">
        <button
          type="button"
          className="pq-btn-primary"
          onClick={props.onSubmit}
          disabled={isSaving || !model.dirty}
          data-testid="pq-submit"
        >
          {isSaving
            ? 'Сохраняем...'
            : model.mode.kind === 'edit'
            ? 'Сохранить'
            : 'Готово'}
        </button>
      </div>
    </>
  );
}

function GenderControl({ value, onChange, disabled }: {
  value: 'male' | 'female' | undefined;
  onChange: (value: 'male' | 'female' | undefined) => void;
  disabled: boolean;
}): ReactNode {
  return (
    <div className="pq-radio-group" role="radiogroup" aria-label="Пол">
      {GENDER_OPTIONS.map((opt) => (
        <label key={opt.value} className="pq-radio-option">
          <input
            type="radio"
            name="pq-gender"
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            disabled={disabled}
            data-testid={`pq-gender-${opt.value}`}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function ScenarioControl({ value, onChange, disabled }: {
  value: 'equal' | 'sponsor' | 'no_work' | undefined;
  onChange: (value: 'equal' | 'sponsor' | 'no_work' | undefined) => void;
  disabled: boolean;
}): ReactNode {
  return (
    <div className="pq-radio-group" role="radiogroup" aria-label="Сценарий">
      {SCENARIO_ORDER.map((scenario) => (
        <label key={scenario} className="pq-radio-option">
          <input
            type="radio"
            name="pq-scenario"
            value={scenario}
            checked={value === scenario}
            onChange={() => onChange(scenario)}
            disabled={disabled}
            data-testid={`pq-scenario-${scenario}`}
          />
          <span>{questionnaireLabelFor(scenario)}</span>
        </label>
      ))}
    </div>
  );
}

function NameControl({ value, onChange, disabled, error }: {
  value: string | undefined;
  onChange: (value: string) => void;
  disabled: boolean;
  error: string | undefined;
}): ReactNode {
  return (
    <div className="pq-field">
      <label htmlFor="pq-name" className="pq-label">
        Имя <span className="pq-required" aria-hidden="true">*</span>
      </label>
      <input
        id="pq-name"
        type="text"
        className="pq-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        maxLength={MAX_DISPLAY_NAME}
        disabled={disabled}
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? 'pq-name-error' : undefined}
        data-testid="pq-name-input"
      />
      {error && (
        <p id="pq-name-error" className="pq-field-error" data-testid="pq-name-error">
          {error}
        </p>
      )}
    </div>
  );
}

function AgeControl({ value, onChange, disabled, error }: {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  disabled: boolean;
  error: string | undefined;
}): ReactNode {
  return (
    <div className="pq-field">
      <label htmlFor="pq-age" className="pq-label">
        Возраст <span className="pq-required" aria-hidden="true">*</span>
      </label>
      <input
        id="pq-age"
        type="number"
        className="pq-input"
        min={MIN_AGE_LOCAL}
        max={MAX_AGE_LOCAL}
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? undefined : Number(v));
        }}
        disabled={disabled}
        aria-invalid={error !== undefined}
        data-testid="pq-age-input"
      />
      {error && <p className="pq-field-error">{error}</p>}
    </div>
  );
}

function CityControl({ value, onChange, disabled, error }: {
  value: string | undefined;
  onChange: (value: string) => void;
  disabled: boolean;
  error: string | undefined;
}): ReactNode {
  return (
    <div className="pq-field">
      <label htmlFor="pq-city" className="pq-label">
        Город <span className="pq-required" aria-hidden="true">*</span>
      </label>
      <select
        id="pq-city"
        className="pq-select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={error !== undefined}
        data-testid="pq-city-select"
      >
        <option value="" disabled>
          Выберите город
        </option>
        {CITY_MVP.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      {error && <p className="pq-field-error">{error}</p>}
    </div>
  );
}

function SelectControl({ id, label, value, options, onChange, disabled }: {
  id: string;
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string | undefined) => void;
  disabled: boolean;
}): ReactNode {
  // CORRECTION01: the unknown-value visible-representation fix.
  //
  // The form may legitimately carry an opaque-bounded string that
  // is not in the local `WORK_EDUCATION_SPHERES` / `EDUCATION_TYPES`
  // corpus (e.g. a server-side enum added after this build, or an
  // existing record with a now-deprecated value). HTML's <select>
  // semantics make such a value invisible — the control would
  // render as if no option were selected, and the user would have
  // no visible indication that the stored value still exists.
  //
  // We inject an ephemeral <option> for any non-empty value that
  // is not in the known options. The option is labelled with a
  // "(неизвестно)" suffix so the user can tell it is an
  // out-of-corpus value (not a normal choice), and the original
  // value is preserved verbatim so an unrelated save does not
  // destroy it.
  const isKnown = options.includes(value);
  const unknownOption =
    value !== '' && !isKnown ? (
      <option key={`__unknown__${value}`} value={value}>
        {value} (неизвестно)
      </option>
    ) : null;
  return (
    <div className="pq-field">
      <label htmlFor={id} className="pq-label">{label}</label>
      <select
        id={id}
        className="pq-select"
        value={value}
        // CORRECTION01: emit the literal empty string when the user
        // picks "Не указано" so formToUpdatePayload can preserve
        // the key on the wire (the backend's *string trichotomy
        // treats "" as clear/NULL, distinct from "key omitted"
        // which is a no-op). Previously this code emitted
        // `undefined` here, which collapsed clear into no-op
        // and silently broke the product-level clear operation.
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={id}
      >
        <option value="">Не указано</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
        {unknownOption}
      </select>
    </div>
  );
}

function TextControl({ id, label, value, onChange, disabled, error, maxLength, placeholder }: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  error: string | undefined;
  maxLength: number;
  placeholder?: string;
}): ReactNode {
  return (
    <div className="pq-field">
      <label htmlFor={id} className="pq-label">{label}</label>
      <input
        id={id}
        type="text"
        className="pq-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={error !== undefined}
        data-testid={id}
      />
      {error && <p className="pq-field-error">{error}</p>}
    </div>
  );
}

function TextareaControl({ id, label, value, onChange, disabled, error, maxLength, placeholder }: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  error: string | undefined;
  maxLength: number;
  placeholder?: string;
}): ReactNode {
  return (
    <div className="pq-field">
      <label htmlFor={id} className="pq-label">{label}</label>
      <textarea
        id={id}
        className="pq-textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={error !== undefined}
        rows={4}
        data-testid={id}
      />
      {error && <p className="pq-field-error">{error}</p>}
    </div>
  );
}
// =============================================================================
// PhotoSection — B-2.B photo media area
// =============================================================================

/**
 * Sort durable photos by canonical position ascending (the server is
 * authoritative; we re-sort defensively).
 */
function sortPersisted(photos: readonly PhotoEntry[]): PhotoEntry[] {
  return [...photos].sort((a, b) => {
    const ap = a.position ?? 0;
    const bp = b.position ?? 0;
    if (ap !== bp) return ap - bp;
    return a.id.localeCompare(b.id);
  });
}

function PhotoSection(
  props: ProfileQuestionnaireViewProps & { isSaving: boolean }
): ReactNode {
  const { model, isSaving } = props;
  const persisted = sortPersisted(model.persistedPhotos);
  const exhausted = isPhotoSlotExhausted(model);
  const visible = visibleSlotCount(model);

  return (
    <section
      className="pq-section"
      data-testid="pq-photos-section"
      aria-label={`Фотографии (${visible}/${PHOTO_MAX_COUNT})`}
    >
      <h2 className="pq-h2">Фотографии</h2>
      <p
        className="pq-lede-small"
        data-testid="pq-photos-count"
      >{`${visible} из ${PHOTO_MAX_COUNT}`}</p>

      <div
        className="pq-photo-grid"
        data-testid="pq-photo-grid"
        role="list"
      >
        {persisted.map((p) => (
          <PhotoThumbnail
            key={`persisted-${p.id}`}
            entry={p}
            isSaving={isSaving}
            onDelete={props.onPhotoDeleteRequested}
          />
        ))}
        {model.transientPhotos.map((p) => (
          <PhotoThumbnail
            key={`transient-${p.id}`}
            entry={p}
            isSaving={isSaving}
            onDelete={(id) => props.onPhotoPreviewReleased(id)}
            onRetry={props.onPhotoUploadRequested}
          />
        ))}
      </div>

      <div className="pq-photo-add">
        <label
          className="pq-btn-secondary"
          htmlFor="pq-photo-input"
          data-testid="pq-photo-add-label"
        >
          Добавить фото
        </label>
        <input
          id="pq-photo-input"
          data-testid="pq-photo-file-input"
          type="file"
          accept={ACCEPTED_PHOTO_MIME_TYPES.join(',')}
          multiple
          disabled={isSaving || exhausted}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            const entries: PhotoEntry[] = [];
            for (const file of files) {
              const rejection = clientPreflightPhotoFile(file);
              if (rejection !== null) {
                // The reducer / view does NOT silently swallow bad
                // inputs; the harness exposes the rejection via
                // the photoUploadFailed effect / serverError path.
                // For client preflight we simply drop the file
                // here (UX-only; the server is still authoritative).
                continue;
              }
              const previewUrl = props.objectUrlPort.createObjectURL(file);
              entries.push({
                id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                kind: 'new-local',
                url: previewUrl,
                fileName: file.name,
                file,
              });
            }
            if (entries.length > 0) {
              props.onPhotoFilesSelected(entries);
            }
            // Reset the input so the same file can be re-selected.
            e.target.value = '';
          }}
        />
        {exhausted && (
          <p
            className="pq-field-error"
            data-testid="pq-photos-max-six"
            role="status"
          >
            {`Достигнут лимит ${PHOTO_MAX_COUNT} фото`}
          </p>
        )}
      </div>
    </section>
  );
}

function PhotoThumbnail({
  entry,
  isSaving,
  onDelete,
  onRetry,
}: {
  readonly entry: PhotoEntry;
  readonly isSaving: boolean;
  readonly onDelete: (id: string) => void;
  readonly onRetry?: (id: string) => void;
}): ReactNode {
  const isPersisted = entry.kind === 'persisted';
  const isDeleting = entry.kind === 'deleting';
  const isUploading = entry.kind === 'uploading';
  const isFailed = entry.kind === 'failed-upload';
  return (
    <div
      className="pq-photo-thumb"
      data-testid={`pq-photo-thumb-${entry.id}`}
      data-photo-kind={entry.kind}
      data-photo-position={entry.position ?? ''}
      role="listitem"
    >
      <img
        src={entry.url}
        alt={entry.fileName ?? 'фото профиля'}
        className="pq-photo-thumb-img"
        // On error we do NOT delete durable state (delivery can be
        // transient or authorization-related). The existing
        // placeholder convention is the canonical fallback; we
        // surface it via CSS (the broken-image pseudo) without
        // mutating state.
        data-testid={`pq-photo-img-${entry.id}`}
      />
      {isPersisted && !isDeleting && (
        <button
          type="button"
          className="pq-photo-delete"
          onClick={() => onDelete(entry.id)}
          disabled={isSaving}
          aria-label="Удалить фото"
          data-testid={`pq-photo-delete-${entry.id}`}
        >
          ×
        </button>
      )}
      {isDeleting && (
        <span
          className="pq-photo-state"
          data-testid={`pq-photo-deleting-${entry.id}`}
          aria-live="polite"
        >
          удаляется...
        </span>
      )}
      {isUploading && (
        <span
          className="pq-photo-state"
          data-testid={`pq-photo-uploading-${entry.id}`}
          aria-live="polite"
        >
          загружается...
        </span>
      )}
      {isFailed && (
        <span
          className="pq-photo-state pq-photo-state-error"
          role="alert"
          data-testid={`pq-photo-failed-${entry.id}`}
        >
          {entry.errorMessage ?? 'ошибка'}
        </span>
      )}
      {isFailed && onRetry && (
        <button
          type="button"
          className="pq-photo-retry"
          onClick={() => onRetry(entry.id)}
          disabled={isSaving}
          aria-label="Повторить загрузку"
          data-testid={`pq-photo-retry-${entry.id}`}
        >
          Повторить
        </button>
      )}
      {entry.kind === 'new-local' && (
        <button
          type="button"
          className="pq-photo-delete"
          onClick={() => onDelete(entry.id)}
          disabled={isSaving}
          aria-label="Удалить выбранное фото"
          data-testid={`pq-photo-remove-pending-${entry.id}`}
        >
          ×
        </button>
      )}
    </div>
  );
}
